import type {
  Connector,
  ConnectorType,
  MetaChannel,
} from "@/features/connectors/types";

/**
 * What the client is adding, in their words — not the connector `type` the
 * backend stores. A phone line is four types (a HeyUp number, a PBX, and the
 * staff-only Twilio and demo ones); a Meta account is one type and three
 * channels. The picker and the list both speak this vocabulary.
 */
export type ChannelKind = "phone" | "email" | "web" | MetaChannel;

/** The channels that open the generic connector form, with the types it offers. */
export type FormChannel = "phone" | "email" | "web";

export const FORM_CHANNEL_TYPES: Record<FormChannel, ConnectorType[]> = {
  phone: ["heyup", "sip", "twilio", "temporary"],
  email: ["email"],
  web: ["web"],
};

/**
 * Channels still being proven with real customers: shown with a "Bêta" mark.
 * Phone lines and the web chat are the established ones.
 */
export const isBeta = (kind: ChannelKind): boolean =>
  kind !== "phone" && kind !== "web";

export const isMetaChannel = (kind: ChannelKind): kind is MetaChannel =>
  kind === "whatsapp" || kind === "messenger" || kind === "instagram";

/** The channel a stored connector belongs to. */
export const channelOf = (
  connector: Pick<Connector, "type" | "configuration">,
): ChannelKind => {
  switch (connector.type) {
    case "meta":
      return connector.configuration?.meta?.channel ?? "whatsapp";
    case "email":
      return "email";
    case "web":
      return "web";
    default:
      return "phone";
  }
};
